import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteSuspense } from "@/components/RouteSuspense";
import { MainLayout } from "@/layouts/MainLayout";
import { PagePlaceholder } from "@/pages/Placeholder";
import {
  collectLeafRoutes,
  collectParentRedirects,
  getDefaultHomePath,
  toRouteSegment,
} from "@/routes/menuConfig";
import { PAGE_LOADERS } from "@/routes/pageLoaders";

const LoginPage = lazy(() => import("@/pages/Login").then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() =>
  import("@/pages/NotFound").then((m) => ({ default: m.NotFoundPage })),
);

/** 预构建 lazy 组件，避免 render 内重复 lazy() */
const LAZY_PAGES = Object.fromEntries(
  Object.entries(PAGE_LOADERS).map(([href, loader]) => [href, lazy(loader)]),
);

/** 按菜单 href 生成懒加载页面元素 */
const createPageElement = (href: string, title: string) => {
  const Page = LAZY_PAGES[href];
  if (!Page) return <PagePlaceholder title={title} />;
  return (
    <RouteSuspense>
      <Page />
    </RouteSuspense>
  );
};

/** 根据菜单生成主布局子路由（入口重定向 + 叶子页 + 父级重定向 + 404） */
const buildLayoutChildren = (): RouteObject[] => {
  const children: RouteObject[] = [
    {
      index: true,
      element: <Navigate to={getDefaultHomePath()} replace />,
    },
  ];
  // 叶子菜单：路径来自 menuConfig，组件来自 pageLoaders
  for (const { href, title } of collectLeafRoutes()) {
    children.push({
      path: toRouteSegment(href),
      element: createPageElement(href, title),
    });
  }
  // 父菜单无独立页时，跳转到第一个子菜单
  for (const { parentSegment, childHref } of collectParentRedirects()) {
    children.push({
      path: parentSegment,
      element: <Navigate to={childHref} replace />,
    });
  }
  children.push({
    path: "*",
    element: (
      <RouteSuspense>
        <NotFoundPage />
      </RouteSuspense>
    ),
  });
  return children;
};

/** 应用路由表 */
export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RouteSuspense>
        <LoginPage />
      </RouteSuspense>
    ),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: buildLayoutChildren(),
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
